import "./reviewPage.css";
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/es";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import BannerMessage from "../common/bannerMessage/bannerMessage";
import ReviewPageSkeleton from "./reviewPageSkeleton/reviewPageSkeleton";

/* Read the appointment in the shop's zone, exactly as viewBooking does — the
   two pages sit next to each other and must not print different times. */
dayjs.extend(utc);
dayjs.extend(timezone);
/* "LL" is the locale's own long date — "August 21, 2026" in English but
   "21 de agosto de 2026" in Spanish, which "MMMM D, YYYY" gets wrong. */
dayjs.extend(localizedFormat);

const MAX_CHARS = 250;
const DEFAULT_TZ = "America/Santo_Domingo";

const ReviewPage = () => {
	const { bookingId } = useParams();
	const navigate = useNavigate();
	const { language } = useContext(CombinedContext);

	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [booking, setBooking] = useState(null);

	const [rating, setRating] = useState(0);
	const [hoverRating, setHoverRating] = useState(0);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [existingReview, setExistingReview] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		/* The router reuses this component across /reviews/:bookingId changes,
		   so clear the previous booking's answers before loading the next one —
		   otherwise its rating, comment and edit mode leak through. */
		setLoading(true);
		setLoadFailed(false);
		setBooking(null);
		setExistingReview(null);
		setIsEditMode(false);
		setRating(0);
		setHoverRating(0);
		setComment("");

		async function load() {
			try {
				const res = await usersRequest.getBooking(bookingId);

				setBooking(res.booking);

				if (res.review) {
					setExistingReview(res.review);
					setRating(res.review.rating);
					setComment(res.review.comment || "");
					setIsEditMode(true);
				}
			} catch (err) {
				console.error("Failed to load booking:", err);
				setLoadFailed(true);
			}
			setLoading(false);
		}
		load();
	}, [bookingId]);

	async function handleSubmit() {
		if (rating === 0) {
			showBanner("error", translations[language].review.errorNoRating);
			return;
		}

		setSubmitting(true);

		try {
			if (isEditMode) {
				await usersRequest.updateReview(existingReview.shopId, existingReview.reviewId, { rating, comment });
			} else {
				await usersRequest.createReview(bookingId, rating, comment);
			}

			navigate(`/booking/${bookingId}`, {
				state: { reviewSubmitted: true, isEdit: isEditMode },
			});
		} catch (err) {
			console.error("Review submit failed:", err);
			showBanner("error", translations[language].review.errorSubmit);
		} finally {
			setSubmitting(false);
		}
	}

	if (loading) return <ReviewPageSkeleton />;

	const t = translations[language].review;

	/* A failed fetch used to sit on "Loading..." forever — say so and offer a way out. */
	if (loadFailed || !booking) {
		return (
			<div className="sf-rv-page">
				<div className="sf-rv-wrap">
					<div className="sf-rv-error">
						<p>{t.errorLoad}</p>
						<button onClick={() => navigate(-1)}>{t.back}</button>
					</div>
				</div>
			</div>
		);
	}

	const displayRating = hoverRating || rating;
	const ratingLabel = t.ratings[displayRating];

	const serviceNames = Array.isArray(booking.services)
		? booking.services.map((s) => (language === "es" ? s.serviceNameEs : s.serviceNameEn) || s.name).filter(Boolean)
		: [];

	const zone = booking.timeZone || DEFAULT_TZ;
	const when = (booking.startTime ? dayjs.tz(booking.startTime, zone) : dayjs(booking.date)).locale(language);
	const shopInitial = booking.shopName?.trim()?.charAt(0)?.toUpperCase() || "?";
	const nearLimit = comment.length > MAX_CHARS * 0.8;

	return (
		<div className="sf-rv-page">
			<div className="sf-rv-wrap">
				{/* ── HEADER ── */}
				<header className="sf-rv-head">
					<button className="sf-rv-back" onClick={() => navigate(-1)}>
						<ArrowLeft size={18} strokeWidth={2} />
						<span>{t.back}</span>
					</button>

					<span className="sf-rv-eyebrow">{t.eyebrow}</span>
					<h1 className="sf-rv-title">{isEditMode ? t.editTitle : t.title}</h1>
				</header>

				{/* ── WHAT IS BEING REVIEWED ── */}
				<section className="sf-rv-context">
					<span className="sf-rv-context-avatar">{shopInitial}</span>
					<span className="sf-rv-context-copy">
						<strong>{booking.shopName}</strong>
						<em>
							{when.format("LL")}
							{booking.startTime && ` · ${when.format("h:mm A")}`}
							{booking.staffName && ` · ${booking.staffName}`}
						</em>
					</span>

					{serviceNames.length > 0 && (
						<span className="sf-rv-chips">
							{serviceNames.map((name, i) => (
								<span key={i} className="sf-rv-chip">
									{name}
								</span>
							))}
						</span>
					)}
				</section>

				{/* ── RATING — the hero ── */}
				<section className={`sf-rv-rating ${displayRating ? "is-set" : ""}`}>
					<p className="sf-rv-ask">{t.ask}</p>

					<div className="sf-rv-stars" role="radiogroup" aria-label={t.ask} onMouseLeave={() => setHoverRating(0)}>
						{[1, 2, 3, 4, 5].map((num) => (
							<button
								type="button"
								key={num}
								role="radio"
								aria-checked={rating === num}
								aria-label={t.ratings[num]}
								className={`sf-rv-star ${displayRating >= num ? "is-on" : ""} ${rating === num ? "is-picked" : ""}`}
								onClick={() => setRating(num)}
								onMouseEnter={() => setHoverRating(num)}
								onFocus={() => setHoverRating(num)}
								onBlur={() => setHoverRating(0)}
							>
								<Star size={44} fill="currentColor" strokeWidth={0.75} />
							</button>
						))}
					</div>

					<div className={`sf-rv-verdict ${displayRating ? "is-set" : ""}`}>{ratingLabel}</div>
				</section>

				{/* ── COMMENT ── */}
				<section className="sf-rv-comment">
					<label className="sf-rv-label" htmlFor="sf-review-text">
						{t.commentLabel}
					</label>
					<textarea
						id="sf-review-text"
						className="sf-rv-textarea"
						placeholder={t.placeholder}
						value={comment}
						maxLength={MAX_CHARS}
						onChange={(e) => setComment(e.target.value)}
					/>
					<div className={`sf-rv-count ${nearLimit ? "is-near" : ""}`}>
						{comment.length}/{MAX_CHARS}
					</div>
				</section>

				{isEditMode && <div className="sf-rv-hint">{t.editHint}</div>}

				{/* ── SUBMIT ── */}
				<button className={`sf-rv-submit ${rating ? "is-ready" : ""}`} onClick={handleSubmit} disabled={submitting}>
					{submitting ? t.submitting : isEditMode ? t.save : t.submit}
				</button>
			</div>

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default ReviewPage;
